import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAuthentication,
  useChannels,
  useChatData,
  useChatMessage,
  useEmitChatReadAllOnce,
  useFlightRooms,
  useMentions,
  useUsers,
} from '@connect-x-app/hybrid-state';
import { AttachmentData, Message, MessageMention } from '@connect-x-app/shared-domain-models';
import { COLORS, LAYERS } from '@connect-x-app/shared-ui';
import { DEFAULT_PAGE_SIZE, mapToChatMessage, useInternetConnection } from '@connect-x-app/shared-utils';
import { ChatMessage } from '@connect-x-app/shared-view-models';
import { useApp } from '@connect-x-app/web-state';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Banner, Icon, ScrollList, ScrollListRef, toastError } from '@connect-x-app/web-ui';

import { ChannelInfoSideModal, ChatEmpty, InputToolbar, MessageInfoSideModal } from './components';
import { AttachmentPreview } from './components/attachmentPreview/AttachmentPreview';
import { ChatMessage as ChatMessageComponent } from './components/ChatMessage/ChatMessage';
import { MentionInfoSideModal } from './components/mentionInfo';

import './Chat.scss';

const FETCH_NEXT_PAGE_DELAY_MS = 250;

type ChatProps = {
  focusedMessageId?: string | null;
};

export const Chat = ({ focusedMessageId }: ChatProps) => {
  const { selectedFlightRoom } = useFlightRooms();
  const { clearFirstUnreadMessage, joinChannel, roomChannels, selectedChannel } = useChannels();
  const { attachmentPreview, setAttachmentPreview } = useApp();
  const { users } = useUsers();
  const { user } = useAuthentication();
  const { getUsedMentions } = useMentions();
  const { t } = useTranslation();
  const { isInternetConnectionOffline } = useInternetConnection();
  const { clearSelectedMentions, isChannelPreviewMode, isPreviewMode, remove, retry, send } = useChatMessage();

  const { chat, fetchNewerMessages, fetchOlderMessages, firstUnreadMessageId, hasLoadedLastPage, isFetching } = useChatData({
    focusedMessageId,
    isChannelPreviewMode,
  });

  useEmitChatReadAllOnce({
    channelId: selectedChannel?.id,
    enabled: !isPreviewMode && !isChannelPreviewMode,
    flightRoomId: selectedFlightRoom?.id,
  });

  const [messageInfoSideModal, setMessageInfoSideModal] = useState<Message | null>(null);
  const [isChannelInfoModalOpen, setIsChannelInfoModalOpen] = useState(false);
  const [mention, setMention] = useState<{ mentionLabel: string; messageId: string } | null>();
  const [attachmentFullScreenPreview, setAttachmentFullScreenPreview] = useState<AttachmentData | null>(null);
  const chatListRef = useRef<ScrollListRef>(null);
  const isFetchingNewerMessagesThrottle = useRef(false);
  const isFetchingOlderMessagesThrottle = useRef(false);
  const hasLandedOnFocusRef = useRef(false);

  const chatMessages: ChatMessage[] = useMemo(() => {
    if (selectedChannel) {
      return chat.map(message => mapToChatMessage(selectedChannel.users, users, message, user));
    }
    return [];
  }, [chat, selectedChannel, users, user]);

  const handlePositionOnSend = () => {
    chatListRef.current?.scrollToBottom();
  };

  const getPendingImage = (currentMessage?: ChatMessage) => {
    if (currentMessage?.pending || !currentMessage?.sent) {
      return currentMessage?.pendingImage ?? null;
    }
    return null;
  };

  const activeMention = useMemo(() => {
    if (mention) {
      const chatMessage = chatMessages.find(chatMessage => chatMessage.id === mention?.messageId);
      return chatMessage?.mentions?.find(m => m.label === mention.mentionLabel);
    }
    return null;
  }, [chatMessages, mention]);

  const onStartReached = () => {
    if (isFetchingOlderMessagesThrottle.current) {
      return;
    }
    isFetchingOlderMessagesThrottle.current = true;
    // When scrolling up to the top, load older messages (previous pages)
    fetchOlderMessages();

    setTimeout(() => {
      isFetchingOlderMessagesThrottle.current = false;
    }, FETCH_NEXT_PAGE_DELAY_MS);
  };

  const onEndReached = useCallback(() => {
    if (isFetchingNewerMessagesThrottle.current || hasLoadedLastPage) {
      return;
    }
    isFetchingNewerMessagesThrottle.current = true;
    // When reaching the bottom, optionally load newer messages (if available)
    fetchNewerMessages();

    setTimeout(() => {
      isFetchingNewerMessagesThrottle.current = false;
    }, FETCH_NEXT_PAGE_DELAY_MS);
  }, [fetchNewerMessages, hasLoadedLastPage]);

  const focusedMessageIndex = useMemo(() => {
    if (focusedMessageId) {
      const foundIndex = chat.findIndex(chatMessage => chatMessage.id === focusedMessageId);
      return foundIndex > -1 ? foundIndex : 0;
    }

    if (!isChannelPreviewMode && firstUnreadMessageId) {
      const foundIndex = chat.findIndex(chatMessage => chatMessage.id === firstUnreadMessageId);
      return foundIndex > -1 ? foundIndex : 0;
    }

    return 0;
  }, [chat, firstUnreadMessageId, focusedMessageId, isChannelPreviewMode]);

  // A focus/unread target exists: anchor the list on it (centered) instead of pinning to the bottom.
  const landOnInitialIndex = !!focusedMessageId || (!isChannelPreviewMode && !!firstUnreadMessageId);

  // Reset the one-shot landing when switching channels.
  useEffect(() => {
    hasLandedOnFocusRef.current = false;
  }, [selectedChannel?.id]);

  // Neither attachment preview is tied to a channel (the staged one lives in the app session
  // store, the full-screen one in local state), so both would survive a channel or flightroom
  // switch. Discard them whenever the selected channel changes.
  useEffect(() => {
    setAttachmentPreview(null);
    setAttachmentFullScreenPreview(null);
  }, [selectedChannel?.id, setAttachmentPreview]);

  // Land on the "New messages" divider once. focusedMessageIndex > 0 means the messages newer than
  // the target have loaded (it is no longer the bottom item), so centering it shows the unread below.
  // selectedChannel?.id is a dependency so the landing re-runs after the reset effect on a channel switch.
  useEffect(() => {
    if (!hasLandedOnFocusRef.current && landOnInitialIndex && focusedMessageIndex > 0) {
      chatListRef.current?.scrollToIndex(focusedMessageIndex);
      hasLandedOnFocusRef.current = true;
    }
  }, [landOnInitialIndex, focusedMessageIndex, selectedChannel?.id]);

  const hasUserInStore = useMemo(() => {
    const usersInStoreSet: Set<string> = new Set(users.map(u => u.externalId));

    return selectedChannel?.users?.some(u => usersInStoreSet.has(u.externalId));
  }, [selectedChannel?.users, users]);

  const showOfflineText = useMemo(() => {
    return !hasUserInStore && isInternetConnectionOffline;
  }, [hasUserInStore, isInternetConnectionOffline]);

  const renderChatMessage = (message: ChatMessage, index: number) => {
    return (
      <ChatMessageComponent
        disabled={isPreviewMode || isChannelPreviewMode}
        focused={!!focusedMessageId && focusedMessageId === message.id}
        key={message.externalId ?? message.id}
        message={message}
        nextMessage={chatMessages[index - 1]}
        onDelete={remove}
        onRetry={retry}
        pendingImage={getPendingImage(message)}
        previousMessage={chatMessages[index + 1]}
        setActiveMention={(mention: MessageMention) =>
          setMention({
            mentionLabel: mention.label,
            messageId: message.id,
          })
        }
        setAttachmentPreview={setAttachmentFullScreenPreview}
        showMessageInfo={() => setMessageInfoSideModal(message)}
        showNewMessagesIndicator={!!firstUnreadMessageId && firstUnreadMessageId === message.id}
      />
    );
  };

  if (!selectedFlightRoom || !selectedChannel || !user) {
    return null;
  }

  const onSend = (message: string, attachment?: File | null) => {
    const mentions = getUsedMentions(message);

    handlePositionOnSend();

    send(message, mentions, attachment);
    clearSelectedMentions();
    clearFirstUnreadMessage(selectedChannel.id);
  };

  const handleJoinChannel = async () => {
    const result = await joinChannel(selectedFlightRoom.id, selectedChannel.id);
    if (!result?.ok) {
      toastError(t('TOAST_ERROR'));
    }
  };

  // The previews cover the chat list, where the info icon would overlap the preview's close icon.
  const isAttachmentPreviewOpen = !!attachmentPreview || !!attachmentFullScreenPreview;

  const chatBottomContent = (() => {
    if (isPreviewMode) {
      return <Banner message={t('TASKS.PREVIEW_MODE.DISCLAIMER')} />;
    }

    if (isChannelPreviewMode) {
      return (
        <Banner
          action={{
            label: t('CHANNEL.PREVIEW_MODE.JOIN'),
            onClick: () => {
              void handleJoinChannel();
            },
            testId: CHANNEL_TEST_IDS.preview.viewChannel,
          }}
          message={t('CHANNEL.PREVIEW_MODE.DISCLAIMER')}
          showIcon={false}
        />
      );
    }

    return (
      <>
        <InputToolbar key={selectedFlightRoom.id} onSend={onSend} />
        {attachmentFullScreenPreview ? (
          <AttachmentPreview
            attachment={attachmentFullScreenPreview}
            file={attachmentFullScreenPreview.id}
            isFullScreen
            onClose={() => setAttachmentFullScreenPreview(null)}
          />
        ) : null}
      </>
    );
  })();

  return (
    <>
      <ChannelInfoSideModal
        channel={selectedChannel}
        isVisible={isChannelInfoModalOpen}
        onClose={() => setIsChannelInfoModalOpen(false)}
        roomId={selectedFlightRoom.id}
        showOfflineText={showOfflineText}
      />
      <MessageInfoSideModal
        channel={selectedChannel}
        message={messageInfoSideModal}
        onClose={() => setMessageInfoSideModal(null)}
        showOfflineText={showOfflineText}
      />
      <MentionInfoSideModal activeMention={activeMention} onClose={() => setMention(null)} showOfflineText={showOfflineText} />
      <div className="chat-container__list">
        <ScrollList
          attachmentPreviewComponent={
            attachmentPreview ? <AttachmentPreview file={attachmentPreview} onClose={() => setAttachmentPreview(null)} /> : null
          }
          data={chatMessages}
          initialIndex={focusedMessageIndex}
          inverted
          isFetching={isFetching}
          key={selectedChannel.id}
          landOnInitialIndex={landOnInitialIndex}
          listEmptyComponent={<ChatEmpty isLoading={isFetching} />}
          onEndReached={onEndReached}
          onStartReached={onStartReached}
          pageSize={DEFAULT_PAGE_SIZE}
          ref={chatListRef}
          renderItem={renderChatMessage}
        />
        {chatBottomContent}
        {roomChannels?.mainChannel.id !== selectedChannel.id && !isChannelPreviewMode && !isAttachmentPreviewOpen ? (
          <Icon
            className="chat-container__info-icon"
            colorName={LAYERS.iconColors['$icon-primary']}
            hoverColorName={COLORS.romeo.$07}
            name="circularInfoOutline"
            onClick={() => setIsChannelInfoModalOpen(true)}
            size={2.4}
            testId={CHANNEL_TEST_IDS.messages.info}
          />
        ) : null}
      </div>
    </>
  );
};
